import React, { useState, useEffect } from 'react';
import { Button, Typography, useToast } from '@crisellevaldez/speculo-ui';
import { Bell, RotateCw, CheckCircle, AlertCircle } from 'lucide-react';
import { Organization } from '../../../../types/organization.types';
import { getFubErrorMessage } from './utils/errorHelpers';

interface FubWebhooksSetupProps {
  selectedOrg?: Organization;
  isConfigured: boolean;
  hasCustomFields: boolean;
  hasStagesAllowlist?: boolean;
  hasTeamMembersSync?: boolean;
  registerFubWebhooks: (orgId: string) => Promise<any>;
  getFubWebhookStatus: (orgId: string) => Promise<any>;
  isRegisteringWebhooks: boolean;
  isFetchingWebhookStatus: boolean;
  onWebhooksSetup: () => void;
  isOpen?: boolean;
}

export const FubWebhooksSetup: React.FC<FubWebhooksSetupProps> = ({
  selectedOrg,
  isConfigured,
  hasCustomFields,
  hasStagesAllowlist = false,
  hasTeamMembersSync = false,
  registerFubWebhooks,
  getFubWebhookStatus,
  isRegisteringWebhooks,
  isFetchingWebhookStatus,
  onWebhooksSetup,
  isOpen = false,
}) => {
  const [webhookStatus, setWebhookStatus] = useState<any>(null);
  const [hasAttemptedSetup, setHasAttemptedSetup] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);

  const { showToast } = useToast();

  // Function to register webhooks
  const handleRegisterWebhooks = async () => {
    if (!selectedOrg?.id) return;

    try {
      setSetupError(null);
      const result = await registerFubWebhooks(selectedOrg.id);

      if (result?.success) {
        // After registering, fetch the status
        await fetchWebhookStatus();
        setHasAttemptedSetup(true);
        showToast({
          message: 'Webhooks registered successfully',
          variant: 'success',
        });

        // Notify parent component
        onWebhooksSetup();
      } else {
        const errorMsg = getFubErrorMessage(result, 'Failed to register webhooks');
        setSetupError(errorMsg);
        showToast({
          message: errorMsg,
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error registering webhooks:', error);
      const errorMsg = getFubErrorMessage(error, 'Failed to register webhooks');
      setSetupError(errorMsg);
      showToast({
        message: errorMsg,
        variant: 'error',
      });
    }
  };

  // Function to fetch webhook status
  const fetchWebhookStatus = async () => {
    if (!selectedOrg?.id) return;

    try {
      setIsLoadingStatus(true);
      const result = await getFubWebhookStatus(selectedOrg.id);

      if (result?.success) {
        setWebhookStatus(result.report);
      } else {
        const errorMsg = getFubErrorMessage(result, 'Failed to fetch webhook status');
        setSetupError(errorMsg);
        showToast({
          message: errorMsg,
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error fetching webhook status:', error);
      const errorMsg = getFubErrorMessage(error, 'Failed to fetch webhook status');
      setSetupError(errorMsg);
      showToast({
        message: errorMsg,
        variant: 'error',
      });
    } finally {
      setIsLoadingStatus(false);
    }
  };

  // Trigger loading webhook status when the component becomes visible
  useEffect(() => {
    if (isOpen && !webhookStatus && !isLoadingStatus && selectedOrg?.id) {
      // Set loading state to prevent multiple calls
      setIsLoadingStatus(true);

      // Load webhook status
      fetchWebhookStatus();
    }
  }, [isOpen, webhookStatus, isLoadingStatus, selectedOrg?.id]);

  // If API key is not configured, custom fields are not set up, stages are not configured, or team members are not synced, don't show this component
  if (!isConfigured || !hasCustomFields || !hasStagesAllowlist || !hasTeamMembersSync) {
    return null;
  }

  return (
    <div>
      <div className="space-y-4">
        <Typography variant="p">
          Register webhooks to receive real-time updates from Follow Up Boss. This ensures that
          Speculo AI stays in sync with your FUB data.
        </Typography>

        {webhookStatus ? (
          <div className="bg-gray-50 dark:bg-white/5 p-4 rounded-md">
            <div className="flex items-center mb-4">
              {webhookStatus.all_required_registered ? (
                <div className="flex items-center text-green-600">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  <Typography variant="p" className="font-medium !mt-0">
                    All required webhooks are registered and active
                  </Typography>
                </div>
              ) : (
                <div className="flex items-center text-amber-600">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  <Typography variant="p" className="font-medium !mt-0">
                    Some webhooks are missing or inactive
                  </Typography>
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              <div>
                <Typography variant="p" className="text-sm font-medium">
                  Total Webhooks:
                </Typography>
                <Typography variant="p" className="text-sm">
                  {webhookStatus.total_webhooks || 0}
                </Typography>
              </div>
              <div>
                <Typography variant="p" className="text-sm font-medium">
                  Active Webhooks:
                </Typography>
                <Typography variant="p" className="text-sm">
                  {webhookStatus.active_webhooks || 0}
                </Typography>
              </div>
              <div>
                <Typography variant="p" className="text-sm font-medium">
                  Inactive Webhooks:
                </Typography>
                <Typography variant="p" className="text-sm">
                  {webhookStatus.inactive_webhooks || 0}
                </Typography>
              </div>
            </div>

            {webhookStatus.required_webhooks?.registered?.length > 0 && (
              <div className="mb-4">
                <div className="max-h-40 overflow-y-auto bg-white dark:bg-white/5 p-2 rounded border dark:border-white/10 text-sm">
                  {webhookStatus.required_webhooks.registered.map((webhook: any, index: number) => (
                    <div key={index} className="mb-1 pb-1 border-b border-gray-100 dark:border-white/10 last:border-0">
                      <span className="font-medium">{webhook.event}</span>
                      <span
                        className={`ml-2 px-1.5 py-0.5 rounded text-xs ${webhook.status === 'Active' ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400' : 'bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-400'}`}
                      >
                        {webhook.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {webhookStatus.required_webhooks?.missing?.length > 0 && (
              <div className="mb-4">
                <Typography variant="p" className="text-sm font-medium mb-1 text-amber-600">
                  Missing Webhooks:
                </Typography>
                <div className="max-h-40 overflow-y-auto bg-white dark:bg-white/5 p-2 rounded border dark:border-white/10 text-sm">
                  {webhookStatus.required_webhooks.missing.map((webhook: string, index: number) => (
                    <div key={index} className="mb-1 pb-1 border-b border-gray-100 dark:border-white/10 last:border-0">
                      {webhook}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex space-x-2">
              <Button
                variant="outline"
                type="button"
                onClick={fetchWebhookStatus}
                disabled={isFetchingWebhookStatus}
                isLoading={isFetchingWebhookStatus}
                className="flex items-center gap-1 h-8"
              >
                <RotateCw className="h-4 w-4" />
                Refresh Status
              </Button>

              <Button
                variant="outline"
                type="button"
                onClick={handleRegisterWebhooks}
                disabled={isRegisteringWebhooks}
                isLoading={isRegisteringWebhooks}
                className="flex items-center gap-1 h-8"
              >
                <Bell className="h-4 w-4" />
                {webhookStatus.all_required_registered
                  ? 'Re-register Webhooks'
                  : 'Register Missing Webhooks'}
              </Button>
            </div>
          </div>
        ) : setupError ? (
          <div className="text-center py-4">
            <Typography variant="p" className="text-sm text-red-500">
              {setupError}
            </Typography>
            <Button variant="outline" type="button" onClick={fetchWebhookStatus} className="mt-2">
              Retry
            </Button>
          </div>
        ) : isLoadingStatus ? (
          <div className="text-center py-4">
            <Typography variant="p" className="text-sm text-gray-500">
              Loading webhook status...
            </Typography>
          </div>
        ) : (
          <div className="flex justify-center py-4">
            <Button
              variant="outline"
              type="button"
              onClick={handleRegisterWebhooks}
              disabled={isRegisteringWebhooks}
              isLoading={isRegisteringWebhooks}
              className="flex items-center gap-1 h-8"
            >
              <Bell className="h-4 w-4" />
              Register Webhooks
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
